// covered-by: frontends/carrie/src/components/icons/__tests__/KanbanIcon.test.tsx
import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/**
 * Hover flag for a decorative glyph that should follow its host control
 * (the wrapping `<a>` / `<button>`), not the 18px icon hit-area.
 *
 * Pass `controlledHover` to drive the flag from the parent instead.
 */
export function useHostHover(controlledHover?: boolean): {
  ref: RefObject<HTMLSpanElement>;
  hover: boolean;
} {
  const ref = useRef<HTMLSpanElement>(null);
  const [uncontrolled, setUncontrolled] = useState(false);
  const isControlled = controlledHover != null;

  useLayoutEffect(() => {
    if (isControlled) return;
    const node = ref.current;
    if (!node) return;
    // why: closest a/button so row hover (label included) drives the glyph, not the 18px icon
    const host = node.closest("a, button") ?? node;
    const enter = () => setUncontrolled(true);
    const leave = () => setUncontrolled(false);
    host.addEventListener("mouseenter", enter);
    host.addEventListener("mouseleave", leave);
    return () => {
      host.removeEventListener("mouseenter", enter);
      host.removeEventListener("mouseleave", leave);
    };
  }, [isControlled]);

  return { ref, hover: isControlled ? controlledHover : uncontrolled };
}

export function prefersReducedMotion(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
